import { prettifyErrors } from '@common/helpers';
import { getSortedIndividualsForDisplay } from '@api/btlApplicationForm/borrowerHelpers';
import { BtlApplicationFormPageEnum, SectionProblems } from '@pages/BtlApplicationFormPage/types';
import type { BuildBtlCaseSidebarParams } from '../types';
import { CaseSidebarItemId } from '../types';

const ITEM_TO_PROBLEMS_SECTION: Partial<Record<CaseSidebarItemId, BtlApplicationFormPageEnum>> = {
  [CaseSidebarItemId.FINANCING]: BtlApplicationFormPageEnum.FINANCING,
  [CaseSidebarItemId.PROPERTY]: BtlApplicationFormPageEnum.PROPERTY,
  [CaseSidebarItemId.EXPENSES]: BtlApplicationFormPageEnum.EXPENSES,
  [CaseSidebarItemId.PRODUCT]: BtlApplicationFormPageEnum.PRODUCT,
  [CaseSidebarItemId.TOP_SLICING]: BtlApplicationFormPageEnum.TOP_SLICING,
  [CaseSidebarItemId.AFFORDABILITY_CHECK]: BtlApplicationFormPageEnum.AFFORDABILITY_CHECK,
  [CaseSidebarItemId.SUBMIT_DIP]: BtlApplicationFormPageEnum.SUBMIT_DIP,
  [CaseSidebarItemId.SOLICITORS]: BtlApplicationFormPageEnum.SOLICITORS,
  [CaseSidebarItemId.BANK_DETAILS]: BtlApplicationFormPageEnum.BANK_DETAILS,
  [CaseSidebarItemId.COMMUNICATION]: BtlApplicationFormPageEnum.COMMUNICATION,
  [CaseSidebarItemId.CUSTOMER_FILES]: BtlApplicationFormPageEnum.DOCUMENTS,
  [CaseSidebarItemId.EVIDENCE]: BtlApplicationFormPageEnum.DOCUMENTS,
  [CaseSidebarItemId.VALUATION]: BtlApplicationFormPageEnum.VALUATION,
  [CaseSidebarItemId.POST_OFFER]: BtlApplicationFormPageEnum.POST_OFFER,
  [CaseSidebarItemId.SUBMIT_APPLICATION]: BtlApplicationFormPageEnum.ADDITIONAL_INFORMATION,
  [CaseSidebarItemId.ADDITIONAL_INFORMATION]: BtlApplicationFormPageEnum.ADDITIONAL_INFORMATION,
};

function hasApplicants(context: BuildBtlCaseSidebarParams): boolean {
  const individualsCount = context.application.borrower?.individuals.length ?? 0;

  if (context.application.applicationType === 'OWNER_OCCUPIED') {
    return individualsCount > 0;
  }

  const companiesCount = context.application.borrower?.companies.length ?? 0;

  return companiesCount + individualsCount > 0;
}

function hasHousehold(context: BuildBtlCaseSidebarParams): boolean {
  const households = context.householdsToHasIndividuals;

  return Boolean(households && Object.keys(households).length > 0);
}

function shouldResolveMissingFields(itemId: CaseSidebarItemId, context: BuildBtlCaseSidebarParams): boolean {
  if (!context.missingFields) {
    return false;
  }

  const isOwnerOccupied = context.application.applicationType === 'OWNER_OCCUPIED';

  switch (itemId) {
    case CaseSidebarItemId.APPLICANTS:
      return hasApplicants(context);
    case CaseSidebarItemId.EXPENSES:
      return hasApplicants(context) && (!isOwnerOccupied || hasHousehold(context));
    case CaseSidebarItemId.PROPERTY:
      return hasApplicants(context) && (!isOwnerOccupied || hasHousehold(context));
    case CaseSidebarItemId.PRODUCT:
    case CaseSidebarItemId.TOP_SLICING:
    case CaseSidebarItemId.AFFORDABILITY_CHECK:
      if (!hasApplicants(context) || (isOwnerOccupied && !hasHousehold(context))) {
        return false;
      }

      return Boolean(context.application.product);
    case CaseSidebarItemId.SUBMIT_DIP:
    case CaseSidebarItemId.SOLICITORS:
    case CaseSidebarItemId.BANK_DETAILS:
    case CaseSidebarItemId.COMMUNICATION:
    case CaseSidebarItemId.CUSTOMER_FILES:
    case CaseSidebarItemId.EVIDENCE:
    case CaseSidebarItemId.VALUATION:
    case CaseSidebarItemId.POST_OFFER:
    case CaseSidebarItemId.SUBMIT_APPLICATION:
    case CaseSidebarItemId.ADDITIONAL_INFORMATION:
      return Boolean(context.isAffordabilityPassed);
    default:
      return true;
  }
}

function extractSectionMissingFieldWarnings(
  section: BtlApplicationFormPageEnum,
  missingFields: SectionProblems,
  formatMessage: BuildBtlCaseSidebarParams['formatMessage'],
): string[] {
  const fieldsToSkip = section === BtlApplicationFormPageEnum.PRODUCT ? ['isCurrentProductSuitable'] : [];
  const sectionData = missingFields[section];

  if (!sectionData) {
    return [];
  }

  const warnings: string[] = [];

  if (section === BtlApplicationFormPageEnum.PRODUCT && !Array.isArray(sectionData)) {
    warnings.push(...prettifyErrors({ missingFields: sectionData.missingFields }, fieldsToSkip).flat());

    if (!sectionData.isCurrentProductSuitable) {
      warnings.push(formatMessage({ id: 'btlApplicationForm.PRODUCT.notSuitable.title' }));
    }

    return warnings;
  }

  if (Array.isArray(sectionData)) {
    return sectionData.flatMap(item => prettifyErrors({ missingFields: item.missingFields }, fieldsToSkip)).flat();
  }

  return prettifyErrors({ missingFields: sectionData.missingFields }, fieldsToSkip).flat();
}

function resolveApplicantsMissingFieldWarnings(context: BuildBtlCaseSidebarParams): string[] {
  const { missingFields, application } = context;

  if (!missingFields) {
    return [];
  }

  const warnings: string[] = [];
  // Missing-fields API entries are ordered by borrower.individualOrder (same as getOrderedIndividuals on BE).
  const orderedIndividuals = application.borrower ? getSortedIndividualsForDisplay(application.borrower) : [];

  const problems = missingFields as SectionProblems;
  const individuals = problems[BtlApplicationFormPageEnum.APPLICANTS_INDIVIDUAL];

  if (Array.isArray(individuals)) {
    individuals.forEach((item, index) => {
      const errors = prettifyErrors(item, []).flat();
      const individual = orderedIndividuals[index];
      const label = individual ? `${individual.firstName} ${individual.lastName}` : undefined;

      errors.forEach(error => {
        warnings.push(label ? `${label}: ${error}` : error);
      });
    });
  }

  const companies = problems[BtlApplicationFormPageEnum.APPLICANTS_COMPANY];

  if (Array.isArray(companies)) {
    companies.forEach((item, index) => {
      const errors = prettifyErrors(item, []).flat();
      const company = application.borrower?.companies[index];
      const label = company?.companyName;

      errors.forEach(error => {
        warnings.push(label ? `${label}: ${error}` : error);
      });
    });
  }

  return warnings;
}

function resolveTopSlicingMissingFieldWarnings(context: BuildBtlCaseSidebarParams): string[] {
  const section = BtlApplicationFormPageEnum.TOP_SLICING;
  const warnings = extractSectionMissingFieldWarnings(
    section,
    context.missingFields as SectionProblems,
    context.formatMessage,
  );

  const householdsToHasIndividuals = context.householdsToHasIndividuals;

  if (householdsToHasIndividuals) {
    const householdsThatNeedIndividuals = Object.entries(householdsToHasIndividuals)
      .filter(([, value]) => value === false)
      .map(([key]) => key);

    if (householdsThatNeedIndividuals.length > 0) {
      warnings.push(context.formatMessage({ id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.noIndividuals.tooltip' }));
    }
  }

  return warnings;
}

export function resolveMissingFieldsWarnings(itemId: CaseSidebarItemId, context: BuildBtlCaseSidebarParams): string[] {
  if (!shouldResolveMissingFields(itemId, context)) {
    return [];
  }

  if (itemId === CaseSidebarItemId.APPLICANTS) {
    return resolveApplicantsMissingFieldWarnings(context);
  }

  if (itemId === CaseSidebarItemId.TOP_SLICING) {
    return resolveTopSlicingMissingFieldWarnings(context);
  }

  const section = ITEM_TO_PROBLEMS_SECTION[itemId];

  if (!section) {
    return [];
  }

  return extractSectionMissingFieldWarnings(section, context.missingFields as SectionProblems, context.formatMessage);
}

export function resolvePropertyStatusWarnings(itemId: CaseSidebarItemId, context: BuildBtlCaseSidebarParams): string[] {
  if (itemId !== CaseSidebarItemId.PROPERTY) {
    return [];
  }

  if (context.application.applicationProperty?.status !== 'REJECTED') {
    return [];
  }

  return [context.formatMessage({ id: 'caseSidebar.warnings.propertyRejected' })];
}
